import { MapClickLayer } from '@sofarocean/atlas/maps';
import { filter } from 'lodash';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDeviceIdList } from '@/api/deviceIdList';
import { useDevices } from '@/api/devices';
import spotterImagePurpleHover from '@/assets/map/spotter-purple-hover.png';
import spotterImagePurpleInactive from '@/assets/map/spotter-purple-inactive.png';
import spotterImagePurpleSelected from '@/assets/map/spotter-purple-selected.png';
import spotterImagePurple from '@/assets/map/spotter-purple.png';
import {
    MapBaseLayerToggle,
    MapControlsStack,
    MyLocationButton,
    SpotterActiveFilterSelector,
} from '@/components/map/LayerControls';
import { SafeMap } from '@/components/map/SafeMap';
import { useSpotterListFilterFns } from '@/components/map/SpotterList';
import { SPOTTER_ICONS, ScaleControl } from '@/components/map/SpotterMap';
import { InspectorPopup } from '@/components/map/inspectors/InspectorPopup';
import { Graticules } from '@/components/map/layers/Graticules';
import { MyLocationLayer } from '@/components/map/layers/MyLocationLayer';
import { MySpotterLayer } from '@/components/map/layers/MySpotterLayer';
import { useMapUiContext } from '@/context/MapUiContext';

export const DEFAULT_GEOLOCATION = { lat: 0, lng: -122.4194 } as const;

export const PublicSpotterMap = () => {
    const { showMyLocation, mapInspector, setMapInspector, spotterListOptions } = useMapUiContext();
    const [searchParams] = useSearchParams();
    const [hoveredSpotter, setHoveredSpotter] = useState<string>();
    const [selectedSpotter, setSelectedSpotter] = useState<string>();
    const spotterFilterFn = useSpotterListFilterFns()[spotterListOptions.filter];

    const { data: deviceIds } = useDeviceIdList({ userFilter: searchParams.get('user') });
    const { data: devicesResult } = useDevices({
        devices: deviceIds?.data?.devices ?? [],
    });

    const filteredDevices = useMemo(() => filter(devicesResult, spotterFilterFn), [devicesResult, spotterFilterFn]);

    function onClickMap() {
        if (mapInspector) {
            setMapInspector(undefined);
        }
        setSelectedSpotter(undefined);
    }

    return (
        <SafeMap mapType="public" allowMapZoomOnScroll center={DEFAULT_GEOLOCATION} zoom={2} weatherLayers={{}}>
            <InspectorPopup />
            <MapClickLayer onClickMap={onClickMap} />
            <ScaleControl maxWidth={160} />
            {Boolean(filteredDevices?.length) && (
                <MySpotterLayer
                    devices={filteredDevices}
                    spotterColor={SPOTTER_ICONS.shared.color}
                    spotterIcons={[
                        { name: 'spotter', url: spotterImagePurple },
                        { name: 'inactive', url: spotterImagePurpleInactive },
                        { name: 'selected', url: spotterImagePurpleSelected },
                        { name: 'highlight', url: spotterImagePurpleHover },
                    ]}
                    onSpotterClick={(spotterId, spotter) => {
                        setSelectedSpotter(spotterId as string);
                        setMapInspector({
                            type: 'spotter',
                            spotterFamily: 'shared',
                            dataSource: 'devices',
                            // pass searchParams to details so the map link can return to the same user map
                            detailsSearchParams: searchParams,
                            spotterId: spotterId as string,
                            position: spotter.position,
                        });
                    }}
                    onSpotterHover={setHoveredSpotter}
                    selectedSpotterId={selectedSpotter}
                    hoveredSpotterId={hoveredSpotter}
                />
            )}
            {showMyLocation && <MyLocationLayer />}
            <Graticules />

            <MapControlsStack
                sx={{
                    right: 4,
                    top: 4,
                }}
            >
                <SpotterActiveFilterSelector spotterFamily="shared" />
                <MyLocationButton />
            </MapControlsStack>
            <MapControlsStack
                sx={{
                    right: 4,
                    bottom: 4,
                }}
            >
                <MapBaseLayerToggle />
            </MapControlsStack>
        </SafeMap>
    );
};
